import type { CustomerSkill, CustomerSkillDetail } from '@harness/shared/protocol';
import { useRef, useState } from 'react';
import { createSkill, deleteSkill, fetchSkill, type SkillSaveResult, updateSkill } from '../api';
import appStyles from '../styles/app.module.css';
import dataStyles from '../styles/data.module.css';
import styles from '../styles/skills.module.css';
import { sectionHeader, TEXT } from '../texts';
import { Avatar } from './Avatar';
import { Confirm } from './Confirm';
import { TrashIcon } from './icons';
import { PageHeader } from './PageHeader';

// Month as a name ("31 Oct 2026"), unambiguous for every reader.
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** The worker checks the same limit (ADR 0024); checked here first so a large file is not sent at all. */
const MAX_BYTES = 100 * 1024;

const TEMPLATE = `---
name: my-skill
description: What the skill does and when Claude should use it.
---

Instructions for Claude, step by step.
`;

export interface SkillApi {
  fetchSkill: (projectId: string, name: string) => Promise<CustomerSkillDetail | null>;
  createSkill: (projectId: string, text: string) => Promise<SkillSaveResult>;
  updateSkill: (projectId: string, name: string, text: string, changedAt: number) => Promise<SkillSaveResult>;
  deleteSkill: (projectId: string, name: string) => Promise<boolean>;
}

const API: SkillApi = { fetchSkill, createSkill, updateSkill, deleteSkill };

interface SkillsProps {
  projectId: string;
  /** null while loading. */
  skills: CustomerSkill[] | null | 'failed';
  /** Reload the list; the socket announces changes of other consultants. */
  onChanged: () => void;
  /** Tests pass a fake. */
  api?: SkillApi;
}

/** `changedAt` of the opened state; null for a new skill. */
type Editing = { name: string | null; changedAt: number | null; text: string; problems: string[] };

/** Customer skills (#76): SKILL.md files all consultants of the customer create, edit and delete. */
export function Skills({ projectId, skills, onChanged, api = API }: SkillsProps): React.JSX.Element {
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const startNew = (text = TEMPLATE): void => setEditing({ name: null, changedAt: null, text, problems: [] });

  /** Fills the open editor, or opens a new skill with the file's text. */
  const load = async (file: File): Promise<void> => {
    if (!file.name.toLowerCase().endsWith('.md')) return setNotice(TEXT.skillNotMarkdown(file.name));
    if (file.size > MAX_BYTES) return setNotice(TEXT.skillTooLarge(file.name));
    const text = await file.text();
    setNotice(null);
    if (editing === null) startNew(text);
    else setEditing({ ...editing, text, problems: [] });
  };

  const open = async (name: string): Promise<void> => {
    const skill = await api.fetchSkill(projectId, name).catch(() => null);
    if (skill === null) {
      setNotice(TEXT.loadFailed);
      onChanged();
      return;
    }
    setNotice(null);
    setEditing({ name: skill.name, changedAt: skill.changedAt, text: skill.text, problems: [] });
  };

  const save = async (): Promise<void> => {
    if (editing === null) return;
    setSaving(true);
    const { name, changedAt, text } = editing;
    const result = await (name === null || changedAt === null ? api.createSkill(projectId, text) : api.updateSkill(projectId, name, text, changedAt)).catch(
      (): SkillSaveResult => ({ ok: false, problems: [] }),
    );
    setSaving(false);
    if (result.ok) {
      setEditing(null);
      setNotice(TEXT.skillSaved(result.skill.name));
      onChanged();
    } else {
      setEditing({ ...editing, problems: result.problems.length > 0 ? result.problems : [TEXT.skillSaveFailed] });
    }
  };

  const remove = async (name: string): Promise<void> => {
    setToDelete(null);
    const ok = await api.deleteSkill(projectId, name).catch(() => false);
    setNotice(ok ? TEXT.skillDeleted(name) : TEXT.skillDeleteFailed(name));
    if (ok) onChanged();
  };

  const fileInput = (
    <input
      ref={input}
      type="file"
      hidden
      accept=".md,text/markdown"
      data-testid="skill-input"
      onChange={(event) => {
        const file = event.target.files?.[0];
        if (file !== undefined) void load(file);
        event.target.value = '';
      }}
    />
  );

  const header = (
    <PageHeader
      {...sectionHeader.skills}
      actions={
        editing === null ? (
          <>
            <button type="button" className={styles.secondaryHeaderButton} onClick={() => input.current?.click()}>
              {TEXT.uploadSkill}
            </button>
            <button type="button" className={appStyles.headerButton} onClick={() => startNew()}>
              {TEXT.newSkill}
            </button>
          </>
        ) : undefined
      }
    />
  );

  const noticeBox = notice !== null && (
    <div className={dataStyles.notice} role="status">
      <span>{notice}</span>
      <button type="button" className={dataStyles.close} aria-label={TEXT.closeNotice} onClick={() => setNotice(null)}>
        ×
      </button>
    </div>
  );

  if (editing !== null) {
    return (
      <>
        {header}
        <main className={dataStyles.main}>
          {fileInput}
          {noticeBox}
          <form
            className={styles.editor}
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <h2 className={styles.editorTitle}>{editing.name === null ? TEXT.skillNewTitle : TEXT.skillEditTitle(editing.name)}</h2>
            {editing.problems.length > 0 && (
              <div className={styles.problems} role="alert">
                {TEXT.skillProblems}
                <ul>
                  {editing.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </div>
            )}
            <label className={styles.label}>
              {TEXT.skillEditorLabel}
              <textarea className={styles.textarea} value={editing.text} spellCheck={false} onChange={(event) => setEditing({ ...editing, text: event.target.value })} />
            </label>
            <p className={styles.hint}>{TEXT.skillEditorHint}</p>
            <div className={styles.editorActions}>
              <button type="submit" className={dataStyles.confirm} disabled={saving}>
                {TEXT.save}
              </button>
              <button type="button" className={dataStyles.secondary} onClick={() => setEditing(null)}>
                {TEXT.cancel}
              </button>
              <button type="button" className={dataStyles.secondary} onClick={() => input.current?.click()}>
                {TEXT.uploadSkill}
              </button>
            </div>
          </form>
        </main>
      </>
    );
  }

  return (
    <>
      {header}
      <main className={dataStyles.main}>
        {fileInput}
        {noticeBox}
        {skills === null || skills === 'failed' ? (
          <p className={dataStyles.muted}>{skills === null ? TEXT.loading : TEXT.loadFailed}</p>
        ) : (
          <div className={dataStyles.tableWrap}>
            <table className={dataStyles.table}>
              <thead>
                <tr>
                  <th>{TEXT.skillName}</th>
                  <th>{TEXT.skillDescription}</th>
                  <th>{TEXT.skillChangedBy}</th>
                  <th>{TEXT.skillDate}</th>
                  <th>
                    <span className={dataStyles.hidden}>{TEXT.dataActions}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => (
                  <Row key={skill.name} skill={skill} onEdit={() => void open(skill.name)} onDelete={() => setToDelete(skill.name)} />
                ))}
              </tbody>
            </table>
            {skills.length === 0 && <p className={dataStyles.empty}>{TEXT.noSkills}</p>}
          </div>
        )}
      </main>
      {toDelete !== null && (
        <Confirm title={TEXT.skillDeleteTitle(toDelete)} text={TEXT.skillDeleteText} action={TEXT.delete} onCancel={() => setToDelete(null)} onConfirm={() => void remove(toDelete)} />
      )}
    </>
  );
}

function Row({ skill, onEdit, onDelete }: { skill: CustomerSkill; onEdit: () => void; onDelete: () => void }): React.JSX.Element {
  return (
    <tr>
      <td>
        <span className={styles.nameCell}>
          /{skill.name}
          {skill.hidden && <span className={styles.badge}>{TEXT.skillHidden}</span>}
        </span>
      </td>
      <td>
        <span className={styles.description}>{skill.description}</span>
        {skill.hidden && <p className={styles.hint}>{TEXT.skillHiddenHint}</p>}
      </td>
      <td>
        {skill.changedBy === null ? (
          <span className={dataStyles.mutedCell}>{TEXT.dataUnknownPerson}</span>
        ) : (
          <span className={dataStyles.person}>
            <Avatar name={skill.changedBy} email={skill.changedBy} />
            {skill.changedBy}
          </span>
        )}
      </td>
      <td className={dataStyles.mutedCell}>{DATE.format(skill.changedAt)}</td>
      <td>
        <span className={styles.actions}>
          <button type="button" className={styles.editButton} aria-label={TEXT.skillEdit(skill.name)} onClick={onEdit}>
            {TEXT.edit}
          </button>
          <button type="button" className={dataStyles.iconButton} aria-label={TEXT.skillDelete(skill.name)} onClick={onDelete}>
            <TrashIcon />
          </button>
        </span>
      </td>
    </tr>
  );
}
